import { useEffect, useMemo, useRef, useState, ChangeEvent } from "react";
import styles from "./Messages.module.scss";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import { useTranslation } from "react-i18next";
import ChatService from "../../api/services/chat.service";
import { ChatMessage, ChatSession } from "../../api/types/chat.types";
import { getCookie } from "../../utils/cookies";

const WS_BASE = process.env.REACT_APP_WS_API_BASE_URL;

const Messages = () => {
  const { t } = useTranslation("translation");
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [typingAdmin, setTypingAdmin] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  const chatBodyRef = useRef<HTMLDivElement | null>(null);

  const sortedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
      ),
    [messages],
  );

  useEffect(() => {
    loadSession();
    const ws = createSocket();
    return () => {
      wsRef.current = null;
      ws?.close();
    };
  }, []);

  useEffect(() => {
    const lastIncoming = [...messages]
      .reverse()
      .find((m) => m.sender_type === "admin");
    if (session && lastIncoming) {
      ChatService.markRead(session.id).catch(() => {});
    }
  }, [messages, session]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [sortedMessages, typingAdmin]);

  const loadSession = async () => {
    try {
      const { data } = await ChatService.getSession();
      setSession(data);
      setMessages(data.messages || []);
    } catch {
      setSession(null);
      setMessages([]);
    }
  };

  const createSocket = () => {
    const token = getCookie("StoreDashboardAccessToken");
    if (!token || !WS_BASE) return null;
    const ws = new WebSocket(
      `${WS_BASE}/chat/ws/store/?token=${encodeURIComponent(token)}`,
    );
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        handleSocketEvent(payload);
      } catch {
        /* ignore parse errors */
      }
    };
    ws.onclose = () => {
      wsRef.current = null;
    };
    return ws;
  };

  const handleSocketEvent = (payload: any) => {
    if (!payload) return;
    switch (payload.type) {
      case "message":
        if (payload.payload?.message) {
          const msg = payload.payload.message as ChatMessage;
          setMessages((prev) => [...prev, msg]);
        }
        break;
      case "session_claimed":
        setSession((prev) =>
          prev ? { ...prev, admin_id: payload.payload?.admin_id } : prev,
        );
        break;
      case "session_closed":
        setSession(null);
        setMessages([]);
        break;
      case "message_read":
        setMessages((prev) =>
          prev.map((m) =>
            m.sender_type === "store" ? { ...m, status: "read" } : m,
          ),
        );
        break;
      case "typing":
        setTypingAdmin(true);
        if (typingTimeout.current) clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => setTypingAdmin(false), 2000);
        break;
      default:
        break;
    }
  };

  const handleSend = async () => {
    if (!input.trim() && !file) return;
    try {
      setUploading(true);
      let attachment_url = "";
      let attachment_type = "";

      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        if (session) {
          formData.append("session_id", session.id.toString());
        }
        const uploadRes = await ChatService.upload(formData);
        attachment_url = uploadRes.data.url;
        attachment_type = uploadRes.data.type;
      }

      const { data } = await ChatService.sendMessage({
        content: input.trim(),
        attachment_url,
        attachment_type,
      });

      setSession(data.session);
      if (data.message) {
        setMessages((prev) => [...prev, data.message]);
      }
      setInput("");
      setFile(null);
    } catch {
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setShowAttachMenu(false);
    }
  };

  const handleAttachClick = (type: "document" | "image") => {
    if (fileInputRef.current) {
      if (type === "image") {
        fileInputRef.current.accept = "image/*,video/*";
      } else {
        fileInputRef.current.accept = "*";
      }
      fileInputRef.current.click();
      setShowAttachMenu(false);
    }
  };

  const sendTyping = () => {
    if (!wsRef.current || !session) return;
    wsRef.current.send(
      JSON.stringify({ type: "typing", session_id: session.id }),
    );
  };

  const getFileNameFromUrl = (url: string) => {
    try {
      const parts = url.split("/");
      return parts[parts.length - 1] || "file";
    } catch {
      return "file";
    }
  };

  const getFileExtension = (filename: string) => {
    const parts = filename.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "FILE";
  };

  const renderStatus = (status: string) => {
    switch (status) {
      case "sent":
        return "✓";
      case "delivered":
        return "✓✓";
      case "read":
        return <span style={{ color: "green", fontWeight: "bold" }}>✓✓</span>;
      default:
        return "";
    }
  };

  const renderMessageBody = (msg: ChatMessage) => {
    if (msg.attachment_url) {
      if (msg.attachment_type?.startsWith("image/")) {
        return (
          <img
            src={msg.attachment_url}
            alt="attachment"
            className={styles.attachmentImage}
          />
        );
      }

      const fileName = getFileNameFromUrl(msg.attachment_url);
      const fileExt = getFileExtension(fileName);
      const isPDF = msg.attachment_type?.includes("pdf") || fileExt === "PDF";

      return (
        <div className={styles.documentCard}>
          <div className={styles.documentIcon}>{isPDF ? "📄" : "📎"}</div>
          <a
            className={styles.documentInfo}
            href={msg.attachment_url}
            target="_blank"
            rel="noreferrer"
          >
            <div className={styles.documentName}>{fileName}</div>
            <div className={styles.documentMeta}>
              {fileExt} • {msg.attachment_type || "Document"}
            </div>
          </a>
        </div>
      );
    }
    return <p>{msg.content}</p>;
  };

  return (
    <div className={styles.messages}>
      <TitleUpdater title={t("messages")} />
      <section className={styles.chat}>
        <header className={styles.chatHeader}>
          <div>
            <h3>{t("messages")}</h3>
            {session?.admin_id ? (
              <span className={styles.status}>{t("connected_to_admin")}</span>
            ) : (
              <span className={styles.status}>{t("waiting_for_admin")}</span>
            )}
          </div>
        </header>
        <div className={styles.chatBody} ref={chatBodyRef}>
          {sortedMessages.map((msg) => (
            <div
              key={msg.id}
              className={`${styles.message} ${
                msg.sender_type === "admin" ? styles.admin : styles.store
              }`}
            >
              {renderMessageBody(msg)}
              <span
                className={`${
                  msg.sender_type === "store"
                    ? styles.store_time
                    : styles.admin_time
                }`}
              >
                {new Date(msg.created).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {msg.sender_type === "store" && msg.status && (
                  <> {renderStatus(msg.status)}</>
                )}
              </span>
            </div>
          ))}
          {!sortedMessages.length && (
            <div className={styles.empty}>
              {t("no_messages") || "No messages"}
            </div>
          )}
          {typingAdmin && (
            <div className={styles.typingIndicator}>
              {t("typing") || "Typing..."}
            </div>
          )}
        </div>

        <footer className={styles.chatFooter}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileChange}
            style={{ display: "none" }}
          />

          <div className={styles.attachContainer}>
            <button
              className={styles.attachButton}
              onMouseEnter={() => setShowAttachMenu(true)}
              onMouseLeave={() => setShowAttachMenu(false)}
              onClick={() => setShowAttachMenu(!showAttachMenu)}
            >
              +
            </button>

            {showAttachMenu && (
              <div
                className={styles.attachMenu}
                onMouseEnter={() => setShowAttachMenu(true)}
                onMouseLeave={() => setShowAttachMenu(false)}
              >
                <button
                  className={styles.attachOption}
                  onClick={() => handleAttachClick("document")}
                >
                  <span className={styles.icon}>📄</span>
                  <span>{t("document")}</span>
                </button>
                <button
                  className={styles.attachOption}
                  onClick={() => handleAttachClick("image")}
                >
                  <span className={styles.icon}>🖼️</span>
                  <span>{t("photos_videos")}</span>
                </button>
              </div>
            )}
          </div>

          {file && <span className={styles.fileName}>{file.name}</span>}

          <input
            type="text"
            placeholder={t("placeholders.write_message") || "Write a message"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            onFocus={sendTyping}
            onInput={sendTyping}
            disabled={uploading}
          />

          <button
            onClick={handleSend}
            disabled={uploading}
            className={styles.sendButton}
          >
            {uploading ? t("loading") || "Sending..." : t("buttons.send")}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default Messages;
